import _round from 'lodash/round';
import _isNumber from 'lodash/isNumber';
import { RefObject } from 'react';
import { EChartsType } from 'echarts';
import findIndex from 'lodash/findIndex';
import { FeatureConfig, RealField, defaultTheme, themeMap } from '../../types';
import {
  CENTER_CONTENT_LINE_HEIGHT,
  CENTER_TITLE_LINE_HEIGHT,
  DEFAULT_OUTER_RADIUS,
  HORIZONTAL_LEGEND_HEIGHT,
  SPIDER_OUTER_RADIUS,
  SUMMARY_GRAPHIC_ID,
  VERTICAL_CHART_MARGIN,
  VERTICAL_LEGEND_MARGIN,
  VERTICAL_LEGEND_WIDTH,
} from '../const';
import { getFormattedValue } from '../util';
import { getCnSelectSetterSnippet, getObjectSetterSnippet } from '../../manager/setter-snippet';
import {
  LabelConfig,
  LegendConfig,
  LabelPosition,
  LegendPosition,
  LabelType,
  CnPieStyle,
  PieRequestConfig,
} from './type';

export interface RealPieFields {
  angleField: RealField[];
  colorField: RealField[];
}

export const getPieFields = (requestConfig?: PieRequestConfig): RealPieFields => {
  if (requestConfig?.metaConfig) {
    return {
      angleField:
        requestConfig.metaConfig?.angleField?.records?.map((record) => {
          return {
            fieldName: record.name,
            ...record.settings,
          };
        }) || [],
      colorField:
        requestConfig.metaConfig?.colorField?.records?.map((record) => {
          return {
            fieldName: record.name,
            ...record.settings,
          };
        }) || [],
    };
  }

  return {
    angleField: requestConfig?.angleField || [],
    colorField: requestConfig?.colorField || [],
  };
};

function getLabelPositionConfig(position: LabelPosition) {
  if (position === 'spider') {
    return {
      position: 'outside',
      alignTo: 'edge',
    };
  }
  return {
    position,
    alignTo: 'none',
  };
}

function getLabelFormatter(type: LabelType, angleField: string, colorField: string) {
  // 数值字段
  if (type === 'angleField') {
    return (param: Record<string, any>) => {
      return `${param.data[angleField]}`;
    };
  }

  // 分类字段
  if (type === 'colorField') {
    return (param: Record<string, any>) => {
      return `${param.data[colorField]}`;
    };
  }

  // 数值百分比
  if (type === 'anglePercent') {
    return (param: Record<string, any>) => `${param.percent || '-'}%`;
  }

  // 分类+数值字段
  if (type === 'colorField+angleField') {
    return (param: Record<string, any>) => {
      return `${param.data[colorField] || ''}\n${param.data[angleField] || ''}`;
    };
  }

  // 分类+数值百分比
  if (type === 'colorField+anglePercent') {
    return (param: Record<string, any>) => {
      return `${param.data[colorField] || ''}\n${param.percent || '-'}%`;
    };
  }
}

/**
 * @description 计算饼图标签
 * @param config
 * @returns
 */
function calculateLabel(
  labelConfig?: LabelConfig,
  angleField?: string,
  colorField?: string,
  legendPosition?: LegendPosition,
) {
  const { type, position } = labelConfig || {};

  if (!labelConfig || !type || !position || !angleField || !colorField) {
    return false;
  }

  const baseLabelConfig = {
    show: true,
    fontSize: 12,
    lineHeight: 16,
    edgeDistance: legendPosition === 'left' || legendPosition === 'right' ? 20 : 78,
  };

  const formatter = getLabelFormatter(type, angleField, colorField);
  const positionConfig = getLabelPositionConfig(position);

  return { ...baseLabelConfig, ...positionConfig, formatter };
}

/**
 * @description 计算饼图图例
 * @param config
 * @returns
 */
function calculateLegend(legendConfig?: LegendConfig) {
  const { position } = legendConfig || {};
  const baseLegendConfig = {
    show: true,
    type: 'scroll',
    itemGap: 20,
    itemHeight: 10,
    itemWidth: 10,
    icon: 'circle',
    pageIconSize: [6.5, 9],
    pageIconColor: '#284CC0',
  };

  if (position === 'top') {
    return {
      legend: {
        ...baseLegendConfig,
        width: 'auto',
        height: HORIZONTAL_LEGEND_HEIGHT,
        top: 0,
        left: 0,
      },
      offset: { top: HORIZONTAL_LEGEND_HEIGHT, bottom: 0, left: 0, right: 0 },
    };
  }

  if (position === 'bottom') {
    return {
      legend: {
        ...baseLegendConfig,
        width: 'auto',
        height: HORIZONTAL_LEGEND_HEIGHT,
        bottom: 0,
        left: 0,
      },
      offset: { bottom: HORIZONTAL_LEGEND_HEIGHT, top: 0, left: 0, right: 0 },
    };
  }

  const baseVerticalLegendConfig = {
    ...baseLegendConfig,
    height: 'auto',
    top: 'middle',
    orient: 'vertical',
    width: `${VERTICAL_LEGEND_WIDTH}%`,
    textStyle: {
      overflow: 'truncate',
      ellipsis: '...',
    },
  };

  const offsetX = `${VERTICAL_CHART_MARGIN}%`;

  if (position === 'right') {
    return {
      legend: {
        ...baseVerticalLegendConfig,
        right: `${VERTICAL_LEGEND_MARGIN}%`,
      },
      offset: {
        right: offsetX,
        left: 0,
        top: 0,
        bottom: 0,
      },
    };
  }

  if (position === 'left') {
    return {
      legend: {
        ...baseVerticalLegendConfig,
        left: `${VERTICAL_LEGEND_MARGIN}%`,
      },
      offset: {
        left: offsetX,
        right: 0,
        top: 0,
        bottom: 0,
      },
    };
  }

  return {
    legend: { show: false },
    offset: { left: 0, right: 0, top: 0, bottom: 0 },
  };
}

/**
 * @description 计算饼图中心区域偏移值
 * @param config
 * @returns
 */

function calculateGraphicOffset(containerHeight: number, position?: LegendPosition) {
  const basePositionY =
    containerHeight * 0.5 - (CENTER_TITLE_LINE_HEIGHT + CENTER_CONTENT_LINE_HEIGHT) * 0.5;
  const offsetX = (100 - VERTICAL_CHART_MARGIN) / 2;

  if (position === 'top') {
    return {
      top: basePositionY + HORIZONTAL_LEGEND_HEIGHT / 2,
      left: 'center',
    };
  }

  if (position === 'bottom') {
    return {
      top: basePositionY - HORIZONTAL_LEGEND_HEIGHT / 2,
      left: 'center',
    };
  }

  if (position === 'right') {
    return { top: basePositionY, left: `${offsetX}%` };
  }

  if (position === 'left') {
    return { top: basePositionY, right: `${offsetX}%` };
  }

  return { top: basePositionY, left: 'center' };
}

export function getRealChartStyle(
  chartStyle: CnPieStyle,
  __featureConfig__: FeatureConfig[] = [],
): CnPieStyle {
  const summaryOptions = __featureConfig__.filter(
    (item) => item.name === 'summary' && item.active,
  )[0];

  if (!summaryOptions) {
    return chartStyle;
  }

  return {
    compositeIndexConfig: {
      type: 'summary',
      ...summaryOptions.options,
    },
    ...chartStyle,
  };
}

function getCompositeIndex(listData: any[], pieFields: RealPieFields, chartStyle: CnPieStyle) {
  const { angleField } = pieFields;
  const realAngleField = angleField?.[0]?.fieldName;
  const { height: containerHeight, innerRadius, compositeIndexConfig } = chartStyle;

  if (!realAngleField || !innerRadius || compositeIndexConfig?.type !== 'summary') {
    return {};
  }

  const centerOffset = calculateGraphicOffset(containerHeight, chartStyle?.legendConfig?.position);

  const sum = listData.reduce((pre, cur) => {
    pre += Number(cur[realAngleField]);
    return pre;
  }, 0);

  return {
    graphic: {
      type: 'group',
      ...centerOffset,
      bounding: 'raw',
      children: [
        {
          type: 'text',
          style: {
            top: 0,
            text: compositeIndexConfig?.title || '共计',
            textAlign: 'center',
            fill: '#666',
            fontSize: 12,
            lineHeight: CENTER_TITLE_LINE_HEIGHT,
          },
        },
        {
          type: 'text',
          top: 16,
          id: SUMMARY_GRAPHIC_ID,
          style: {
            text: _round(
              sum,
              typeof compositeIndexConfig?.decimal === 'number' &&
                compositeIndexConfig?.decimal >= 0
                ? compositeIndexConfig?.decimal
                : 2,
            ),
            textAlign: 'center',
            fill: '#1A1A1A',
            fontSize: 24,
            lineHeight: CENTER_CONTENT_LINE_HEIGHT,
            fontWeight: 'bold',
          },
        },
      ],
    },
  };
}

function getPieCharOptions(listData: any[], pieFields: RealPieFields, chartStyle: CnPieStyle) {
  const realAngleField = pieFields?.angleField?.[0]?.fieldName; // 数值字段
  const realColorField = pieFields?.colorField?.[0]?.fieldName; // 分类字段
  const realAngleFieldAliasName = pieFields?.angleField?.[0]?.aliasName; // 分类字段别名
  const angleFieldAliasName = getFormattedValue(
    realAngleFieldAliasName || realAngleField,
    realAngleField,
  );

  const { themeColor, labelConfig, innerRadius, legendConfig } = chartStyle;
  const outerRadius =
    labelConfig?.position === 'outside' || labelConfig?.position === 'spider'
      ? SPIDER_OUTER_RADIUS
      : DEFAULT_OUTER_RADIUS;
  const { legend, offset } = calculateLegend(legendConfig);

  return {
    color: themeMap[themeColor || defaultTheme].color,
    // 提示框配置：https://echarts.apache.org/zh/option.html#tooltip
    tooltip: {
      trigger: 'item',
      textStyle: {
        fontSize: 12,
        color: '#666',
      },
      formatter: (param: { data: { [key: string]: unknown }; color: string; percent: number }) => {
        return `
          <div>
            <div style="display:flex; align-items:center">
              <div style="height:8px; width:8px; background:${
                param.color
              }; border-radius:50%; margin-right: 4px"></div>
              <div>${param.data[realColorField]}</div>
            </div>
            <div style="display:flex">
              <div style="margin-right: 24px">${angleFieldAliasName || realAngleField}</div>
              <div>${param.data[realAngleField]}</div>
            </div>
            <div style="display:flex">
              <div style="margin-right: 24px">占比</div> 
              <div>${param.percent}%</div>
            </div>
          </div>`;
      },
    },
    legend,
    media: [
      {
        query: { maxWidth: 500 },
        option: {
          series: [
            {
              label: {
                edgeDistance: 20,
              },
            },
          ],
        },
      },
    ],
    dataset: {
      source: listData,
    },
    series: [
      {
        type: 'pie',
        minShowLabelAngle: 10,
        encode: {
          itemName: realColorField,
          value: realAngleField,
        },
        label: calculateLabel(labelConfig, realAngleField, realColorField),
        radius: [`${outerRadius * 100 * (innerRadius || 0)}%`, `${outerRadius * 100}%`],
        ...offset,
      },
    ],
    ...getCompositeIndex(listData, pieFields, chartStyle),
  };
}

const createCompositeIndexSetter = () => {
  const getHistorySummary = (prop: any) => {
    const featureConfig: FeatureConfig[] = prop.getNode().getPropValue('__featureConfig__') || [];
    const summaryOptions = featureConfig?.filter(
      (item) => item.name === 'summary' && item.active,
    )[0];
    return summaryOptions;
  };

  return {
    name: 'compositeIndexConfig',
    title: '复合指标',
    display: 'accordion',
    setter: getObjectSetterSnippet({
      initialValue: (prop: any) => {
        const summaryOptions = getHistorySummary(prop);
        return {
          type: summaryOptions ? 'summary' : 'none',
          title: summaryOptions?.options?.title || '',
          decimal: summaryOptions?.options?.decimal || '',
        };
      },
      items: [
        {
          name: 'type',
          isRequired: true,
          title: '指标类型',
          setter: getCnSelectSetterSnippet({
            options: [
              {
                label: '无',
                value: 'none',
              },
              {
                label: '中心结果汇总',
                value: 'summary',
              },
            ],
          }),
        },
        {
          title: '汇总标题',
          name: 'title',
          setter: 'CnI18nSetter',
        },
        {
          title: '保留小数位数',
          name: 'decimal',
          setter: {
            componentName: 'NumberSetter',
            props: {
              min: 0,
            },
          },
        },
      ],
    }),
  };
};

const getPieLegendChangeCallback =
  (chartStyle: CnPieStyle, realColorField: string, realAngleField: string) =>
  ({
    selectedItems,
    datasets,
    echartRef,
  }: {
    selectedItems: Record<string, boolean>;
    datasets: Array<{ source?: Record<string, unknown> }>;
    echartRef: RefObject<EChartsType>;
  }) => {
    const dataset = datasets[0];
    if (!Array.isArray(dataset.source)) {
      return;
    }

    const datasetReset = {
      ...dataset,
      source: dataset.source?.map((sourceItem) => {
        return selectedItems?.[sourceItem[realColorField]] === true
          ? { ...sourceItem }
          : { ...sourceItem, [realAngleField]: 0 };
      }),
    };

    const graphics = echartRef?.current?.getOption().graphic || [];

    if (
      Array.isArray(graphics) &&
      findIndex(graphics[0]?.elements || [], (g: any) => g?.id === SUMMARY_GRAPHIC_ID) !== -1
    ) {
      const decimal = chartStyle.compositeIndexConfig?.decimal;
      const sum = datasetReset?.source?.reduce((pre, cur) => {
        pre += Number(cur[realAngleField]);
        return pre;
      }, 0);

      echartRef?.current?.setOption({
        dataset: datasetReset,
        graphic: [
          {
            id: SUMMARY_GRAPHIC_ID,
            $action: 'merge',
            style: {
              text: _round(sum, _isNumber(decimal) && decimal >= 0 ? decimal : 2),
              textAlign: 'center',
            },
          },
        ],
      });

      return;
    }

    echartRef?.current?.setOption({
      dataset: datasetReset,
    });
  };

export {
  getPieCharOptions,
  calculateLabel,
  calculateLegend,
  calculateGraphicOffset,
  createCompositeIndexSetter,
  getPieLegendChangeCallback,
};
